import { navActions } from '@ce/core';
import type { NavTarget } from '@ce/core';
import { btn, divider, kicker, t } from '../styles';
import { PulseArc, Sheen } from '../components/Ambient';
import { propsOf, type WidgetProps } from './context';

type Tone = 'info' | 'highlight' | 'alert';

/**
 * Alert is the one tone that ignores the theme.
 *
 * Every other colour follows the attendee's chosen look, but an alert has to
 * read as an alert in all of them — in a green theme a green warning is just
 * decoration. So it carries a fixed amber and only borrows text colour.
 */
const ALERT = '232, 163, 61';

export function Banner({ ctx, inst }: WidgetProps) {
  const { state, dispatch, bundle } = ctx;
  const p = propsOf<{
    tone: Tone; title: string; body: string; actionLabel: string;
    target: NavTarget | null; dismissible: boolean;
  }>(inst);

  if (state.dismissedBanners.includes(inst.id)) return null;

  const alert = p.tone === 'alert';
  const colors = alert
    ? { border: `rgba(${ALERT}, 0.55)`, bg: `rgba(${ALERT}, 0.12)`, accent: `rgb(${ALERT})` }
    : p.tone === 'highlight'
      ? { border: 'var(--color-accent-700)', bg: 'var(--color-accent-900)', accent: 'var(--color-accent-300)' }
      : { border: divider, bg: 'var(--color-surface)', accent: 'var(--color-accent-300)' };

  const open = () => p.target && navActions(p.target, bundle, state.day).forEach(dispatch);

  return (
    <div
      style={{
        position: 'relative', overflow: 'hidden', marginBottom: 14, padding: '13px 14px',
        borderRadius: 'var(--radius-md)', border: `1px solid ${colors.border}`, background: colors.bg,
        display: 'flex', gap: 12, alignItems: 'flex-start',
      }}
    >
      {p.tone === 'highlight' && <Sheen delay={0.4} strength={0.06} />}
      <span style={{ flex: 'none', width: 3, alignSelf: 'stretch', borderRadius: 2, background: colors.accent, position: 'relative' }} />
      <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <div style={{ font: '500 14px/1.3 var(--font-body)' }}>{p.title}</div>
        {p.body && <div style={{ fontSize: 12.5, lineHeight: 1.55, color: t(0.68), marginTop: 5 }}>{p.body}</div>}
        {p.target && (
          <button type="button" onClick={open} style={btn('ghost', { fontSize: 12.5, marginTop: 6, paddingInline: 0, color: colors.accent })}>
            {p.actionLabel || 'Learn more'} {'\u203a'}
          </button>
        )}
      </div>
      {p.dismissible && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => dispatch({ type: 'dismissBanner', id: inst.id })}
          style={{ flex: 'none', width: 26, height: 26, border: 0, borderRadius: 6, background: 'transparent', color: t(0.5), cursor: 'pointer', fontSize: 16, lineHeight: 1, position: 'relative' }}
        >
          {'\u00d7'}
        </button>
      )}
    </div>
  );
}

export function RichText({ inst }: WidgetProps) {
  const p = propsOf<{ heading: string; body: string; align: 'left' | 'center' }>(inst);
  if (!p.heading && !p.body) return null;
  return (
    <div style={{ marginTop: 24, textAlign: p.align }}>
      {p.heading && <div style={{ font: '500 17px/1.25 var(--font-body)', letterSpacing: '-0.01em' }}>{p.heading}</div>}
      {p.body && <p style={{ fontSize: 13.5, lineHeight: 1.65, color: t(0.68), margin: p.heading ? '8px 0 0' : 0 }}>{p.body}</p>}
    </div>
  );
}

export function CtaButton({ ctx, inst }: WidgetProps) {
  const { state, dispatch, bundle } = ctx;
  const p = propsOf<{ label: string; note: string; target: NavTarget | null; variant: 'primary' | 'secondary' }>(inst);
  return (
    <div style={{ marginTop: 20 }}>
      <button
        type="button"
        onClick={() => p.target && navActions(p.target, bundle, state.day).forEach(dispatch)}
        style={btn(p.variant, { width: '100%', height: 46, borderRadius: 'var(--radius-md)', fontSize: 14.5 })}
      >
        {p.label}
      </button>
      {p.note && <div style={{ fontSize: 11, color: t(0.42), marginTop: 8, textAlign: 'center' }}>{p.note}</div>}
    </div>
  );
}

export function Spacer({ inst }: WidgetProps) {
  const p = propsOf<{ height: number; line: boolean }>(inst);
  return (
    <div style={{ height: p.height, display: 'flex', alignItems: 'center' }}>
      {p.line && (
        <div style={{ width: '100%', height: 1, background: `linear-gradient(to right, transparent, ${divider} var(--rule-fade), ${divider} calc(100% - var(--rule-fade)), transparent)` }} />
      )}
    </div>
  );
}

export function TicketCta({ ctx, inst }: WidgetProps) {
  const { state, dispatch } = ctx;
  const p = propsOf<{ title: string; body: string; action: string; hideWhenBadge: boolean }>(inst);
  if (p.hideWhenBadge && state.credential) return null;
  return (
    <div
      style={{
        position: 'relative', overflow: 'hidden', marginTop: 16, padding: 16, borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-accent-700)', background: 'var(--color-accent-900)',
      }}
    >
      <Sheen delay={1} />
      <PulseArc size={150} top={-56} right={-40} opacity={0.26} delay={0.8} />
      <div style={{ position: 'relative' }}>
        <div style={kicker(9.5, 'var(--color-accent-300)')}>{p.title}</div>
        {p.body && <div style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.7), margin: '9px 0 13px', maxWidth: 290 }}>{p.body}</div>}
        <button type="button" onClick={() => dispatch({ type: 'go', screen: 'tickets' })} style={btn('primary', { height: 42, borderRadius: 9, fontSize: 13.5 })}>
          {p.action}
        </button>
      </div>
    </div>
  );
}

export function MerchStrip({ ctx, inst }: WidgetProps) {
  const { bundle, dispatch } = ctx;
  const p = propsOf<{ title: string; limit: number }>(inst);
  const items = (bundle.merch ?? []).slice(0, p.limit);
  if (!items.length) return null;
  const cur = bundle.event.currency;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '24px 0 11px' }}>
        <div style={kicker()}>{p.title}</div>
        <button type="button" onClick={() => dispatch({ type: 'go', screen: 'shop' })} style={btn('ghost', { fontSize: 12 })}>Shop all</button>
      </div>
      <div className="scroll" style={{ display: 'flex', gap: 10, overflowX: 'auto', margin: '0 -20px', padding: '0 20px 4px' }}>
        {items.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => dispatch({ type: 'go', screen: 'shop' })}
            style={{
              flex: 'none', width: 148, textAlign: 'left', padding: 13, borderRadius: 'var(--radius-md)',
              border: `1px solid ${divider}`, background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 104,
            }}
          >
            <span style={{ font: '500 13.5px/1.25 var(--font-body)' }}>{m.name}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <span style={{ font: '500 14px/1 var(--font-body)', color: 'var(--color-accent-300)' }}>{cur}{m.price}</span>
              {m.exclusive && <span style={{ ...kicker(8.5, t(0.5), 0.1) }}>Exclusive</span>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
